import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { T, PageHeader, Section, Field, Alert, Row } from '../components/UI';

export default function JobForm() {
  const { user }  = useAuth();
  const userLocation = user?.selectedLocation || '';
  const navigate  = useNavigate();


  const [locations,   setLocations]   = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [services,    setServices]    = useState([]);
  const [form, setForm] = useState({
    location:userLocation, dealership:'', stockNum:'', vinNum:'', rego:'',
    description:'', coreServices:[], extraNotes:'', delTo:'',
    poEntries:[{ poNumber:'', price:'', services:[], invoiceNumber:'', extraNotes:'' }],
  });
  const [submitting,   setSubmitting]   = useState(false);
  const [submitted,    setSubmitted]    = useState(false);
  const [error,        setError]        = useState(null);
  const [fieldErrors,  setFieldErrors]  = useState({});
  const [dupCheck,     setDupCheck]     = useState(null);
  const [dupChecking,  setDupChecking]  = useState(false);
  const [allHistory,   setAllHistory]   = useState([]);
  const [showHistory,  setShowHistory]  = useState(false);
  const dupTimer = useRef(null);
  const [showDupModal,   setShowDupModal]   = useState(false);
  const [confirmProceed, setConfirmProceed] = useState(false);
  const [poDupWarnings,  setPoDupWarnings]  = useState({});
  const [poDupConfirmed, setPoDupConfirmed] = useState({});

  useEffect(() => {
    api.get('/jobs/locations').then(({data})=>setLocations(data.locations)).catch(()=>{});
    api.get('/jobs/dealerships').then(({data})=>setDealerships(data.dealerships)).catch(()=>{});
    api.get('/jobs/services').then(({data})=>setServices((data.services||[]).slice().sort((a,b)=>a.name.localeCompare(b.name)))).catch(()=>{});
  }, []);

  const handleStockChange = (val) => {
    setForm(f=>({...f, stockNum:val.toUpperCase()}));
    setFieldErrors(fe=>({...fe, stockNum:false}));
    setDupCheck(null); setAllHistory([]); setShowHistory(false); setConfirmProceed(false);
    clearTimeout(dupTimer.current);
    if (val.length >= 3) {
      dupTimer.current = setTimeout(async () => {
        setDupChecking(true);
        try {
          const dupRes  = await api.get('/jobs/check-duplicate', { params:{ stockNum:val.toUpperCase(), date:new Date(new Date().getTime() + 8*60*60*1000).toISOString().split('T')[0] } });
          setDupCheck(dupRes.data);
          const histRes = await api.get('/jobs/search', { params:{ q:val.toUpperCase(), field:'stockNum', exact:'true' } });
          setAllHistory(histRes.data.jobs || []);
        } catch {}
        finally { setDupChecking(false); }
      }, 600);
    }
  };

  const checkPODuplicate = async (poNumber, entryIdx) => {
    if (!poNumber || poNumber.length < 3) { setPoDupWarnings(w=>({...w,[entryIdx]:null})); return; }
    try {
      const today = new Date(new Date().getTime() + 8*60*60*1000).toISOString().split('T')[0];
      const { data } = await api.get('/jobs/check-po', { params:{ poNumber:poNumber.toUpperCase(), date:today } });
      setPoDupWarnings(w=>({...w,[entryIdx]:data.found?data:null}));
      if (data.found) setPoDupConfirmed(p=>({...p,[entryIdx]:false}));
    } catch {}
  };

  const toggleService = (svc) => setForm(f=>({
    ...f, coreServices: f.coreServices.includes(svc) ? f.coreServices.filter(s=>s!==svc) : [...f.coreServices, svc],
  }));

  const validate = () => {
    const errs = {};
    if (!form.dealership) errs.dealership = true;
    if (!form.stockNum)   errs.stockNum   = true;
    if (!form.vinNum)     errs.vinNum     = true;
    if (!form.description) errs.description = true;
    if (form.coreServices.length === 0) errs.coreServices = true;
    if (!form.delTo)      errs.delTo      = true;
    form.poEntries.forEach((entry, idx) => {
      if (!entry.price || parseFloat(entry.price) <= 0) errs[`price_${idx}`] = true;
    });
    form.poEntries.forEach((entry, idx) => {
      if (poDupWarnings[idx] && !poDupConfirmed[idx]) errs[`podup_${idx}`] = true;
    });
    // "Others" selected: PO1 uses shared top Extra Notes, PO2+ uses their own field
    if (form.coreServices.includes('Others') && !form.extraNotes.trim()) {
      errs.extraNotesTop = true;
    }
    form.poEntries.forEach((entry, idx) => {
      if (idx === 0) return;
      if ((entry.services||[]).includes('Others') && !(entry.extraNotes||'').trim()) {
        errs[`extraNotes_${idx}`] = true;
      }
    });
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { window.scrollTo({top:0,behavior:'smooth'}); return; }
    if (dupCheck?.found && !confirmProceed) { setShowDupModal(true); return; }
    doSubmit();
  };

  const doSubmit = async () => {
    setShowDupModal(false); setSubmitting(true); setError(null);
    try {
      const payload = {
        ...form,
        poEntries: form.poEntries.map((e,i) => ({
          poNumber:      e.poNumber.toUpperCase().trim(),
          price:         parseFloat(e.price)||0,
          services:      i===0 ? form.coreServices : (e.services||[]),
          invoiceNumber: (e.invoiceNumber||'').toUpperCase().trim(),
          extraNotes:    i===0 ? (form.extraNotes||'') : (e.extraNotes||''),
        })),
        price:        parseFloat(form.poEntries[0]?.price)||0,
        poNumber:     form.poEntries[0]?.poNumber?.toUpperCase().trim() || '',
        hasPO:        form.poEntries.some(e => e.poNumber?.trim()),
        coreServices: form.coreServices,
        extraNotes:   [form.extraNotes?.trim() ? (form.poEntries.length>1?'PO1: ':'')+form.extraNotes.trim() : '', ...form.poEntries.slice(1).map((e,i)=>e.extraNotes?.trim() ? `PO${i+2}: `+e.extraNotes.trim() : '')].filter(Boolean).join(' | '),
      };
      await api.post('/jobs', payload);
      setSubmitted(true);
    } catch (err) { setError(err.response?.data?.message || 'Submission failed.'); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setForm({location:'',dealership:'',stockNum:'',vinNum:'',rego:'',description:'',coreServices:[],extraNotes:'',delTo:'',poEntries:[{poNumber:'',price:'',services:[]}]});
    setSubmitted(false); setError(null); setFieldErrors({}); setDupCheck(null); setConfirmProceed(false); setAllHistory([]); setShowHistory(false);
  };

  if (submitted) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif' }}>
      <div style={{ textAlign:'center', maxWidth:'360px', padding:'20px' }}>
        <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'var(--green-bg)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
          <i className="ti ti-check" style={{ fontSize:'28px', color:'var(--green)' }} aria-hidden="true"/>
        </div>
        <h2 style={{ fontSize:'18px', fontWeight:'500', color:'var(--text)', marginBottom:'8px' }}>Job Submitted</h2>
        <p style={{ color:'var(--muted)', marginBottom:'24px', fontSize:'13px' }}>
          {form.poEntries.some(e=>e.poNumber) ? 'Saved with PO number(s).' : 'Saved. PO number(s) can be added later via Excel.'}
        </p>
        <div style={{ display:'flex', gap:'10px', justifyContent:'center' }}>
          <button onClick={()=>navigate('/jobs')} style={T.btnSecondary}>View All Jobs</button>
          <button onClick={reset} style={T.btnPrimary}>New Job</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={T.page}>

      {/* Duplicate modal */}
      {showDupModal && dupCheck?.jobs?.length > 0 && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div style={{ background:'var(--surface)', borderRadius:'var(--radius-lg)', padding:'24px', maxWidth:'480px', width:'100%', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)', marginBottom:'8px' }}>Duplicate Entry Warning</div>
            <p style={{ color:'var(--muted)', fontSize:'13px', marginBottom:'14px' }}>
              Stock# <strong style={{ color:'var(--blue)', fontFamily:'JetBrains Mono,monospace' }}>{form.stockNum}</strong> already has {dupCheck.count} entry/entries today. Proceed?
            </p>
            <div style={{ background:'var(--bg)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:'14px', maxHeight:'180px', overflowY:'auto' }}>
              {dupCheck.jobs.map(j=>(
                <div key={j._id} style={{ padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:'12px', color:'var(--muted)' }}>
                  <span style={{ color:'var(--blue)', fontFamily:'JetBrains Mono,monospace', fontWeight:'500' }}>{j.stockNum}</span>
                  <span style={{ marginLeft:'8px' }}>{j.dealership}</span>
                  <span style={{ marginLeft:'8px' }}>{(j.coreServices||[]).join(', ')}</span>
                  <span style={{ marginLeft:'8px', color:'var(--green)' }}>${j.price}</span>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={()=>{setConfirmProceed(true);doSubmit();}} style={T.btnPrimary}>Submit Anyway</button>
              <button onClick={()=>setShowDupModal(false)} style={T.btnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <PageHeader title="New Job Entry" onBack={()=>navigate(-1)}
        actions={
          <span style={{ fontSize:'12px', color:'var(--muted)', fontFamily:'JetBrains Mono,monospace' }}>
            {new Date().toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric'})}
          </span>
        }
      />

      <form onSubmit={handleSubmit} style={{ maxWidth:'680px', margin:'0 auto', padding:'20px 16px 120px' }}>
        {/* Field-specific errors summary */}
        {Object.keys(fieldErrors).length > 0 && (
          <div style={{ background:'var(--red-bg)', border:'1px solid rgba(163,45,45,0.2)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:'14px', fontSize:'13px', color:'var(--red)' }}>
            <div style={{ fontWeight:'500', marginBottom:'4px', display:'flex', alignItems:'center', gap:'6px' }}>
              <i className="ti ti-alert-circle" aria-hidden="true"/> Please fix the following:
            </div>
            <ul style={{ paddingLeft:'16px', margin:0 }}>
              {fieldErrors.location   && <li>Location is required</li>}
              {fieldErrors.dealership && <li>Dealership is required</li>}
              {fieldErrors.stockNum    && <li>Stock Number is required</li>}
              {fieldErrors.vinNum      && <li>VIN Number is required</li>}
              {fieldErrors.description && <li>Description is required</li>}
              {fieldErrors.coreServices && <li>At least one service must be selected</li>}
              {fieldErrors.extraNotesTop && <li>Extra Notes is required (Others service selected for PO 1)</li>}
              {Object.keys(fieldErrors).filter(k=>k.startsWith('extraNotes_')).map(k=>(
                <li key={k}>Extra Notes is required for PO Entry {parseInt(k.split('_')[1])+1} (Others service selected)</li>
              ))}
              {fieldErrors.delTo       && <li>Delivered To is required</li>}
              {Object.keys(fieldErrors).filter(k=>k.startsWith('price_')).map(k=>(
                <li key={k}>Price is required for PO Entry {parseInt(k.split('_')[1])+1}</li>
              ))}
              {Object.keys(fieldErrors).filter(k=>k.startsWith('podup_')).map(k=>(
                <li key={k}>Confirm or clear duplicate PO for Entry {parseInt(k.split('_')[1])+1}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Blue intro banner */}
        <div style={{ background:'var(--blue)', borderRadius:'var(--radius-lg)', padding:'16px 20px', marginBottom:'16px', color:'#fff', display:'flex', alignItems:'center', gap:'12px' }}>
          <div style={{ width:'40px', height:'40px', background:'rgba(255,255,255,0.15)', borderRadius:'var(--radius)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <i className="ti ti-file-description" style={{ fontSize:'22px' }} aria-hidden="true"/>
          </div>
          <div>
            <div style={{ fontSize:'14px', fontWeight:'500', marginBottom:'2px' }}>New Job Entry</div>
            <div style={{ fontSize:'12px', opacity:0.8 }}>Stock # and VIN are required fields</div>
          </div>
        </div>

        {/* Location banner */}
        <div style={{ background:'var(--blue-light)', border:'1px solid rgba(10,110,180,0.2)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:'12px', display:'flex', alignItems:'center', gap:'8px' }}>
          <i className="ti ti-map-pin" style={{ color:'var(--blue)', fontSize:'16px', flexShrink:0 }} aria-hidden="true"/>
          <span style={{ fontSize:'13px', color:'var(--blue)', fontWeight:'500' }}>{userLocation || 'No location set'}</span>
          <span style={{ fontSize:'12px', color:'var(--muted)', marginLeft:'auto' }}>Your location from login</span>
        </div>

        {/* Dealership — open tiles */}
        <Section title="Select Dealership *" accent error={fieldErrors.dealership ? 'Required — please select a dealership' : null}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:'8px' }}>
            {dealerships.map(d=>{
              const sel = form.dealership === d.name;
              return (
                <button key={d._id} type="button"
                  onClick={()=>{setForm(f=>({...f,dealership:d.name})); setFieldErrors(fe=>({...fe,dealership:false}));}}
                  style={{ padding:'12px 10px', borderRadius:'var(--radius)', border:`2px solid ${sel?'var(--blue)':'var(--border-md)'}`, background:sel?'var(--blue)':'var(--surface)', color:sel?'#fff':'var(--text)', fontSize:'13px', fontWeight:sel?'600':'400', cursor:'pointer', textAlign:'center', fontFamily:'inherit', transition:'all .15s', display:'flex', flexDirection:'column', alignItems:'center', gap:'6px' }}>
                  <i className="ti ti-building" style={{ fontSize:'20px', opacity:sel?1:0.4 }} aria-hidden="true"/>
                  {d.name}
                </button>
              );
            })}
          </div>
        </Section>

        {/* PO Entry — moved right after dealership */}
        {/* PO Entries */}
        <Section title="Pricing & Purchase Order" accent>
          <p style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'14px' }}>
            Add one or more PO + Price entries. PO 1 uses the general services above.
          </p>
          {form.poEntries.map((entry, idx) => (
            <div key={idx} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'14px', marginBottom:'10px', position:'relative' }}>
              <div style={{ fontSize:'12px', fontWeight:'500', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'10px' }}>
                PO Entry {form.poEntries.length > 1 ? idx+1 : ''}
              </div>
              <Row cols={3}>
                <Field label="PO Number">
                  <input value={entry.poNumber}
                    onChange={e=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],poNumber:e.target.value.toUpperCase()}; setForm(f=>({...f,poEntries:entries})); checkPODuplicate(e.target.value, idx);}}
                    style={T.inputMono} placeholder="PO number (optional)"/>
                </Field>
                <Field label="Invoice Number">
                  <input value={entry.invoiceNumber||''}
                    onChange={e=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],invoiceNumber:e.target.value.toUpperCase()}; setForm(f=>({...f,poEntries:entries}));}}
                    style={{ ...T.inputMono, borderColor:'rgba(83,74,183,0.3)' }} placeholder="Invoice # (optional)"/>
                </Field>
                <Field label={`Price ($) *`} error={fieldErrors[`price_${idx}`] ? 'Required' : null}>
                  <div style={{ position:'relative' }}>
                    <span style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', color:'var(--muted)', fontSize:'14px' }}>$</span>
                    <input type="number" value={entry.price}
                      onChange={e=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],price:e.target.value}; setForm(f=>({...f,poEntries:entries})); setFieldErrors(fe=>({...fe,[`price_${idx}`]:false}));}}
                      style={{ ...T.inputMono, paddingLeft:'26px', borderColor:fieldErrors[`price_${idx}`]?'var(--red)':'var(--border-md)' }}
                      placeholder="0.00" step="0.01" min="0"/>
                  </div>
                </Field>
              </Row>

              {/* PO dup warning */}
              {poDupWarnings[idx] && !poDupConfirmed[idx] && (
                <Alert type="error">
                  <div>
                    PO <strong>{entry.poNumber}</strong> already used on a different date ({poDupWarnings[idx].count} entry/entries)
                    <div style={{ marginTop:'8px', display:'flex', gap:'8px' }}>
                      <button type="button" onClick={()=>setPoDupConfirmed(p=>({...p,[idx]:true}))} style={{ ...T.btnPrimary, padding:'5px 12px', fontSize:'12px', background:'var(--red)' }}>Proceed Anyway</button>
                      <button type="button" onClick={()=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],poNumber:''}; setForm(f=>({...f,poEntries:entries})); setPoDupWarnings(w=>({...w,[idx]:null}));}} style={{ ...T.btnSecondary, padding:'5px 12px', fontSize:'12px' }}>Clear PO</button>
                    </div>
                  </div>
                </Alert>
              )}
              {poDupWarnings[idx] && poDupConfirmed[idx] && (
                <p style={{ fontSize:'11px', color:'var(--amber)', marginTop:'4px' }}>Proceeding with duplicate PO</p>
              )}

              {/* Services for PO 2+ */}
              {idx > 0 && (
                <div style={{ marginTop:'12px' }}>
                  <label style={T.label}>Services for PO {idx+1}</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'6px' }}>
                    {services.map(svc=>{
                      const sel=(entry.services||[]).includes(svc.name);
                      const usedInOther = svc.name !== 'Others' && form.poEntries.some((e,i)=>i!==idx&&(e.services||[]).includes(svc.name));
                      return (
                        <button key={svc._id} type="button" disabled={usedInOther}
                          onClick={()=>{ if(usedInOther)return; const entries=[...form.poEntries]; const svcs=entries[idx].services||[]; entries[idx]={...entries[idx],services:sel?svcs.filter(s=>s!==svc.name):[...svcs,svc.name]}; setForm(f=>({...f,poEntries:entries})); }}
                          style={{ padding:'5px 12px', borderRadius:'20px', border:`1px solid ${usedInOther?'var(--border)':sel?'var(--blue)':'var(--border-md)'}`, background:usedInOther?'var(--bg)':sel?'var(--blue-light)':'var(--surface)', color:usedInOther?'var(--hint)':sel?'var(--blue)':'var(--muted)', fontSize:'12px', cursor:usedInOther?'not-allowed':'pointer', fontFamily:'inherit' }}>
                          {sel&&<i className="ti ti-check" style={{ marginRight:'3px', fontSize:'11px' }} aria-hidden="true"/>}
                          {svc.name}{usedInOther?' (used)':''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {idx===0 && <p style={{ fontSize:'11px', color:'var(--muted)', marginTop:'8px' }}>Uses general services selected above</p>}

              {/* Per-PO Extra Notes — only for PO2+ when Others is selected */}
              {idx>0 && (entry.services||[]).includes('Others') && (
                <div style={{ marginTop:'12px' }}>
                  <Field label="Extra Notes *" error={fieldErrors[`extraNotes_${idx}`] ? 'Required when "Others" is selected' : null}>
                    <textarea value={entry.extraNotes||''}
                      onChange={e=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],extraNotes:e.target.value}; setForm(f=>({...f,poEntries:entries})); setFieldErrors(fe=>({...fe,[`extraNotes_${idx}`]:false}));}}
                      rows={2} style={{ ...T.input, resize:'vertical', borderColor:fieldErrors[`extraNotes_${idx}`]?'var(--red)':'var(--border-md)' }}
                      placeholder="Describe the 'Others' service for this PO..."/>
                  </Field>
                </div>
              )}

              {!entry.poNumber && <p style={{ fontSize:'11px', color:'var(--amber)', marginTop:'4px' }}>No PO — this row will be highlighted in Excel</p>}

              {form.poEntries.length > 1 && (
                <button type="button" onClick={()=>{const entries=form.poEntries.filter((_,i)=>i!==idx); setForm(f=>({...f,poEntries:entries}));}}
                  style={{ position:'absolute', top:'10px', right:'10px', ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)', fontSize:'12px' }}>
                  Remove
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={()=>setForm(f=>({...f,poEntries:[...f.poEntries,{poNumber:'',price:'',services:[],invoiceNumber:''}]}))}
            style={{ width:'100%', background:'transparent', border:'1px dashed var(--border-md)', borderRadius:'var(--radius)', padding:'10px', color:'var(--blue)', fontSize:'13px', fontWeight:'500', cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
            <i className="ti ti-plus" aria-hidden="true"/> Add Another PO / Price
          </button>
        </Section>
        {/* Vehicle */}
        <Section title="Vehicle Details" accent>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
            <Field label="Stock # *" error={fieldErrors.stockNum?'Required':null}>
              <input value={form.stockNum} onChange={e=>handleStockChange(e.target.value)}
                style={{ ...T.inputMono, borderColor:fieldErrors.stockNum?'var(--red)':dupCheck?.found?'var(--amber)':'var(--border-md)' }}
                placeholder="e.g. 40383C"/>
              {dupChecking && <p style={{ fontSize:'11px', color:'var(--muted)', marginTop:'3px' }}>Checking...</p>}
              {!dupChecking && dupCheck?.found && !confirmProceed && (
                <div style={{ marginTop:'4px', background:'var(--amber-bg)', border:'1px solid rgba(133,79,11,0.2)', borderRadius:'var(--radius)', padding:'6px 10px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:'11px', color:'var(--amber)' }}>{dupCheck.count} entry today</span>
                  <button type="button" onClick={()=>setShowDupModal(true)} style={{ background:'none', border:'none', color:'var(--amber)', fontSize:'11px', cursor:'pointer', fontFamily:'inherit', textDecoration:'underline' }}>View</button>
                </div>
              )}
              {!dupChecking && dupCheck?.found===false && form.stockNum.length>=3 && (
                <p style={{ fontSize:'11px', color:'var(--green)', marginTop:'3px' }}>✓ No duplicate today</p>
              )}
            </Field>
            <Field label="VIN *" error={fieldErrors.vinNum?'Required':null}>
              <input value={form.vinNum} onChange={e=>{setForm(f=>({...f,vinNum:e.target.value.toUpperCase()}));setFieldErrors(fe=>({...fe,vinNum:false}));}}
                style={{ ...T.inputMono, borderColor:fieldErrors.vinNum?'var(--red)':'var(--border-md)' }} placeholder="VIN number"/>
            </Field>
            <Field label="Rego">
              <input value={form.rego} onChange={e=>setForm(f=>({...f,rego:e.target.value.toUpperCase()}))}
                style={T.inputMono} placeholder="Optional"/>
            </Field>
          </div>

          {/* History */}
          {!dupChecking && allHistory.length > 0 && (
            <div style={{ marginTop:'8px' }}>
              <button type="button" onClick={()=>setShowHistory(h=>!h)}
                style={{ ...T.btnSecondary, width:'100%', justifyContent:'space-between', fontSize:'12px' }}>
                <span>{allHistory.length} historical entry(s) for this stock#</span>
                <i className={`ti ti-chevron-${showHistory?'up':'down'}`} aria-hidden="true"/>
              </button>
              {showHistory && (
                <div style={{ border:'1px solid var(--border)', borderTop:'none', borderRadius:'0 0 var(--radius) var(--radius)', overflow:'auto', maxHeight:'240px' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'520px' }}>
                    <thead>
                      <tr>
                        {['Stock #','Date','Dealership','Services','Price','PO','Status'].map(h=>(
                          <th key={h} style={T.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allHistory.map((j,i)=>(
                        <tr key={j._id} style={{ background:i%2===0?'var(--surface)':'var(--bg)' }}>
                          <td style={{ ...T.td, fontFamily:'JetBrains Mono,monospace', color:'var(--blue)', fontWeight:'500' }}>{j.stockNum||'—'}</td>
                          <td style={{ ...T.td, fontFamily:'JetBrains Mono,monospace', color:'var(--muted)' }}>{new Date(j.createdAt).toLocaleDateString('en-AU')}</td>
                          <td style={T.td}>{j.dealership}</td>
                          <td style={T.td}>{(j.coreServices||[]).join(', ')||'—'}</td>
                          <td style={{ ...T.td, fontFamily:'JetBrains Mono,monospace', color:'var(--green)', fontWeight:'500' }}>${(j.price||0).toFixed(2)}</td>
                          <td style={T.td}>{j.poNumber || <span style={{ color:'var(--hint)' }}>—</span>}</td>
                          <td style={T.td}>
                            <span style={{ fontSize:'11px', fontWeight:'500', padding:'2px 8px', borderRadius:'20px', background:j.status==='pending_po'?'var(--amber-bg)':'var(--green-bg)', color:j.status==='pending_po'?'var(--amber)':'var(--green)' }}>
                              {j.status==='pending_po'?'Pending PO':'Submitted'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* Description */}
        <Section title="Description *" accent error={fieldErrors.description ? "Required" : null}>
          <Field label="Description">
            <input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}
              style={T.input} placeholder="e.g. 2024 Hyundai Tucson, White"/>
          </Field>
        </Section>

        {/* Services */}
        <Section title="General Services *" accent error={fieldErrors.coreServices ? "Required" : null}>
          {services.length === 0 ? (
              <p style={{ color:'var(--muted)', fontSize:'13px' }}>No services configured. Ask the owner to add services in Settings.</p>
            ) : (
              <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                {services.map(svc => {
                  const sel = form.coreServices.includes(svc.name);
                  return (
                    <button key={svc._id} type="button" onClick={()=>toggleService(svc.name)}
                      style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'7px 14px', borderRadius:'20px', border:`2px solid ${sel?'var(--blue)':'var(--border-md)'}`, background:sel?'var(--blue)':'var(--surface)', color:sel?'#fff':'var(--muted)', fontSize:'13px', fontWeight:sel?'600':'400', cursor:'pointer', fontFamily:'inherit', transition:'all .15s', whiteSpace:'nowrap' }}>
                      {sel && <i className="ti ti-check" style={{ fontSize:'12px' }} aria-hidden="true"/>}
                      {svc.name}
                    </button>
                  );
                })}
              </div>
            )}
          <div style={{ marginTop:'14px' }}>
            <Field label={form.coreServices.includes('Others') && fieldErrors.extraNotesTop ? 'Extra Notes *' : 'Extra Notes'} error={form.coreServices.includes('Others') && fieldErrors.extraNotesTop ? 'Required when "Others" is selected' : null}>
              <textarea value={form.extraNotes} onChange={e=>{setForm(f=>({...f,extraNotes:e.target.value})); setFieldErrors(fe=>({...fe,extraNotesTop:false}));}}
                rows={2} style={{ ...T.input, resize:'vertical', borderColor:(form.coreServices.includes('Others') && fieldErrors.extraNotesTop)?'var(--red)':'var(--border-md)' }} placeholder="Any additional details..."/>
            </Field>
          </div>
        </Section>

        {/* Delivered To */}
        <Section title="Delivered To *" accent error={fieldErrors.delTo ? "Required" : null}>
          <Field label="Delivered To">
            <input value={form.delTo} onChange={e=>setForm(f=>({...f,delTo:e.target.value}))}
              style={T.input} placeholder="e.g. Front lot, Bay 3"/>
          </Field>
        </Section>


        {/* Bottom bar */}
        <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'var(--surface)', borderTop:'1px solid var(--border)', padding:'12px 16px', paddingBottom:'calc(12px + env(safe-area-inset-bottom, 60px))', display:'flex', justifyContent:'space-between', alignItems:'center', zIndex:150 }}>
          <div style={{ fontSize:'12px', color:'var(--muted)' }}>
            <span style={{ fontWeight:'500', color:'var(--text)' }}>{user?.name}</span>
            {form.dealership && <span> · {form.dealership}</span>}
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button type="button" onClick={()=>navigate(-1)} style={T.btnSecondary}>Cancel</button>
            <button type="submit" disabled={submitting} style={{ ...T.btnPrimary, opacity:submitting?0.6:1 }}>
              {submitting ? 'Saving...' : 'Submit Job'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
